import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { USERNAME, PASSWORD } from '../../config';
import DashboardSection from '../../Component/common/Dashboard/DashboardSection';
import DateFilter from '../../Component/common/datefilter/DateFilter';
import { getTodos } from '../../api/todo';
import { getLeadsWithAuth } from '../../api/leads';
import { getContacts } from '../../api/contacts';
import { getInvoices } from '../../api/invoice';
import './dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  //leads, contacts,tasks and invoces state management
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [errorLeads, setErrorLeads] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [errorContacts, setErrorContacts] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [completedTasksCount, setCompletedTasksCount] = useState(0);
  const [futureTasksCount, setFutureTasksCount] = useState(0);
  const [totalTasksCount, setTotalTasksCount] = useState(0);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [errorTasks, setErrorTasks] = useState('');
  // Invoice state management
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [errorInvoices, setErrorInvoices] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoadingLeads(true);
      setLoadingContacts(true);
      setErrorLeads('');
      setErrorContacts('');
      setLoadingTasks(true);
      setErrorTasks('');
      setLoadingInvoices(true);
      setErrorInvoices('');

      try {
        const [leadsResponse, contactsResponse, todosResponse, invoicesResponse] = await Promise.all([
          getLeadsWithAuth(USERNAME, PASSWORD, { fields: 'id,vc_enquiry_id,posted_on,responded,created_at' }),
          getContacts(USERNAME, PASSWORD),
          getTodos(),
          getInvoices(USERNAME, PASSWORD)
        ]);

        console.log('leadsResponse.data:', leadsResponse.data);
        console.log('contactsResponse.data:', contactsResponse.data);
        console.log('todosResponse.data:', todosResponse.data);
        console.log('invoicesResponse.data:', invoicesResponse.data);

        const leadsData = Array.isArray(leadsResponse.data) ? leadsResponse.data : leadsResponse.data?.leads || [];
        const contactsData = Array.isArray(contactsResponse.data) ? contactsResponse.data : contactsResponse.data?.contacts || [];
        const todosData = Array.isArray(todosResponse.data) ? todosResponse.data : todosResponse.data?.todos || [];
        const invoicesData = Array.isArray(invoicesResponse.data) ? invoicesResponse.data : invoicesResponse.data?.todos || [];

        setLeads(leadsData.map(lead => ({
          ...lead,
          posted_on: lead.posted_on || lead.created_at
        })));

        setContacts(contactsData);

        const pendingTasks = (() => {
          const now = new Date();
          return todosData.filter(todo => {
            if (todo.status !== 'pending') return false;
            if (!todo.targetDate) return true; // If no targetDate, consider as pending
            const dueDate = new Date(todo.targetDate);
            return dueDate <= now;
          });
        })();
        setPendingTasksCount(pendingTasks.length);

        const completedTasks = todosData.filter(todo => todo.status === 'completed');
        setCompletedTasksCount(completedTasks.length);

        const now = new Date();
        const futureTasks = todosData.filter(todo => {
          if (!todo.targetDate) return false;
          const dueDate = new Date(todo.targetDate);
          return dueDate > now && todo.status === 'pending';
        });
        setFutureTasksCount(futureTasks.length);

        const totalTasks = todosData.filter(todo => todo.status !== 'completed');
        setTotalTasksCount(totalTasks.length);
        console.log('Total Tasks:', totalTasks.length);
        console.log('Total Task IDs:', totalTasks.map(task => task.id));

        console.log('Pending Task IDs:', pendingTasks.map(task => task.id));
        console.log('Future Task IDs:', futureTasks.map(task => task.id));

        setInvoices(invoicesData);


      } catch (err) {
        setErrorLeads('Failed to fetch leads');
        setErrorContacts('Failed to fetch contacts');
        setErrorTasks('Failed to fetch tasks');
        setErrorInvoices('Failed to fetch invoices');
        console.error('API Error:', err);
      } finally {
        setLoadingLeads(false);
        setLoadingContacts(false);
        setLoadingTasks(false);
        setLoadingInvoices(false);
      }
    };

    fetchData();
  }, []);

  const filterByDateRange = (items, dateField = 'posted_on') => {
    if (!fromDate && !toDate) return items;

    const startDate = fromDate ? new Date(fromDate) : new Date(0);
    startDate.setHours(0, 0, 0, 0);

    const endDate = toDate ? new Date(toDate) : new Date();
    endDate.setHours(23, 59, 59, 999);

    return items.filter(item => {
      if (!item[dateField]) return false;
      
      try {
        const itemDate = new Date(item[dateField]);
        if (isNaN(itemDate.getTime())) return false;
        
        return itemDate >= startDate && itemDate <= endDate;
      } catch (e) {
        console.warn('Invalid date format for item:', item.id, item[dateField]);
        return false;
      }
    });
  };

  // Apply filters using the posted_on field (fallback to created_at)
  const filteredLeads = filterByDateRange(leads);
  const filteredContacts = filterByDateRange(contacts);
  const filteredInvoices = filterByDateRange(invoices, 'invoice_date');


  // Calculate lead metrics
  const totalLeads = filteredLeads.length;
  const statusCounts = filteredLeads.reduce((acc, lead) => {
    const status = lead.responded || 'New';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Calculate contact metrics
  const totalContacts = filteredContacts.length;
  const manufacturerCount = filteredContacts.filter(c => c.contact_type === 'manufacturer').length;
  const buyerCount = filteredContacts.filter(c => c.contact_type === 'buyer').length;
  const contractorCount = filteredContacts.filter(c => c.contact_type === 'contractor').length;

    // Calculate Invoice metrics
  const totalInvoices = filteredInvoices.length;

  // Cards data for each section

  //leads cards
  const leadCards = [
    { key: 'total-leads', title: 'Total Leads', count: totalLeads, onClick: () => navigate('/buy-leads') },
    ...Object.entries(statusCounts).map(([status, count]) => ({
      key: `status-${status.toLowerCase().replace(/\s+/g, '-')}`,
      title: status,
      count
    }))
  ];
//contacts cards
  const contactCards = [
    { key: 'total-contacts', title: 'Total Contacts', count: totalContacts, onClick: () => navigate('/contacts') },
    { key: 'manufacturers', title: 'Manufacturers', count: manufacturerCount },
    { key: 'buyers', title: 'Buyers', count: buyerCount },
    { key: 'contractors', title: 'Contractors', count: contractorCount }
  ];

  //tasks cards
  const taskCards = [
    { key: 'total-tasks', title: 'Total Tasks', count: totalTasksCount, onClick: () => navigate('/todo') },
    { key: 'pending-tasks', title: 'Pending Tasks', count: pendingTasksCount, onClick: () => navigate('/todo') },
    // { key: 'completed-tasks', title: 'Completed Tasks', count: completedTasksCount, onClick: () => navigate('/todo') },
    { key: 'future-tasks', title: 'Future Tasks', count: futureTasksCount, onClick: () => navigate('/todo') }
  ];

  //invoice cards
  const invoiceCards = [
    { key: 'total-invoices', title: 'Total Invoices', count: totalInvoices, onClick: () => navigate('/invoice') }
  ];

  return (
    <div className="dashboard-container">
      <h2>Dashboard</h2>

      <DateFilter
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
      />

      <div className="filter-status">
        <p>
          Showing {filteredLeads.length} of {leads.length} leads | 
          Showing {filteredContacts.length} of {contacts.length} contacts
        </p>
        {fromDate || toDate ? (
          <p>
            Date Range: {fromDate || 'Earliest'} to {toDate || 'Latest'}
          </p>
        ) : null}
      </div>

      <DashboardSection
        title="Leads"
        loading={loadingLeads}
        error={errorLeads}
        cards={leadCards}
      />

      <DashboardSection
        title="Contacts"
        loading={loadingContacts}
        error={errorContacts}
        cards={contactCards}
      />

      <DashboardSection
        title="Tasks"
        loading={loadingTasks}
        error={errorTasks}
        cards={taskCards}
      />

      <DashboardSection
        title="Invoices"
        loading={loadingInvoices}
        error={errorInvoices}
        cards={invoiceCards}
      />
    </div>
  );
};

export default Dashboard;
